import { uuid } from "src/app/support/uuid";

export interface ReportDesignItem<T = any> {
  icon: string;
  type:
    | "custom"
    | "image"
    | "group"
    | "group-horizontal"
    | "group-vertical"
    | "tab"
    | "text"
    | "textarea"
    | "table"
    | "signature"
    | "customer-signature"
    | "spacer"
    | "title"
    | "preview-watermark"
    | "tags"
    | "checklist"
    | "check"
    | "empty"
    | "selectbox";

  title: string;
  childTitle?: string;
  extended?: boolean;
  titleReadOnly?: boolean;
  name?: string;
  canAdd?: (template: ReportDesignItemConfig[]) => boolean;
  component?: any;
  disableRemove?: boolean;
  enableHideFeature?: boolean;
  disableSortable?: boolean;
  baseConfig?: T;
  hideIconsSection?: boolean;
  showHeaderTitle?: boolean;
  sticky?: boolean;
  friendlyName?: string;
}

export type ReportDesignItemConfig =
  | ReportDesignerItemImage
  | ReportDesignerItemTab
  | ReportDesignerItemGroupVertical
  | ReportDesignItemText
  | ReportDesignItemGrid
  | ReportDesignItemSignature
  | ReportDesignerItemTitle
  | ReportDesignerItemSpacer
  | ReportDesignItemSelectbox;

type ReportDesignBaseConfig = Record<string, any> & {
  hidden?: boolean;
};

export interface ReportDesignItemTemplate extends ReportDesignItem {
  config: ReportDesignBaseConfig;
}

export interface ReportDesignerItemImage extends ReportDesignItem {
  config: ReportDesignerItemImageConfig;
}

export type ReportDesignerItemImageConfig = ReportDesignBaseConfig & {
  data: string;
  value: string;
  caption: string;
  align: "left" | "center" | "right";
};

export interface ReportDesignerItemTags extends ReportDesignItem {
  config: ReportDesignerItemTagsConfig;
}

export type ReportDesignerItemTagsConfig = ReportDesignBaseConfig & {
  title: string;
  category: string;
};

export interface ReportDesignerItemTab extends ReportDesignItem {
  title: string;
  config: ReportDesignerItemTabConfig;
}

export type ReportDesignerItemTabConfig = ReportDesignBaseConfig & {
  title: string;
  name: string;
  items: ReportDesignItemConfig[];
  tabs: ReportDesignerItemTabItem[];
};

export interface ReportDesignerItemTabItem {
  name: string;
  items: ReportDesignItemConfig[];
  index: number;
}

export interface ReportDesignerItemCustom extends ReportDesignItem {
  component: any;
  config: ReportDesignerItemCustomConfig;
}

export type ReportDesignerItemCustomConfig = ReportDesignBaseConfig & {
  html: (item: ReportDesignItemConfig) => string;
};

export interface ReportDesignerItemImageValue extends ReportDesignerItemImage {
  value: string | undefined;
}

export interface ReportDesignerItemGroupVertical extends ReportDesignItem {
  config: ReportDesignerItemGroupVerticalConfig;
}

export type ReportDesignerItemGroupVerticalConfig = ReportDesignBaseConfig & {
  group: ReportDesignItemConfig[];
  name: string;
  cols: string;
};

export interface ReportDesignerItemGroupHorizontal extends ReportDesignItem {
  config: ReportDesignerItemGroupVerticalConfig;
}

export type ReportDesignerItemGroupHorizontalConfig = ReportDesignBaseConfig & {
  group: ReportDesignItemConfig[];
  name: string;
};

export interface ReportDesignerItemSpacer extends ReportDesignItemTemplate {
  config: ReportDesignItemSpacerConfig;
}

export type ReportDesignItemSpacerConfig = ReportDesignBaseConfig & {
  separator?: boolean;
  size?: string;
  dashed?: boolean;
};

export interface ReportDesignerItemTitle extends ReportDesignItemTemplate {
  config: ReportDesignItemTitleConfig;
}

export interface ReportDesignerItemTitleValue extends ReportDesignerItemTitle {
  titleValue: string;
  size: string;
  alignment: string;
}

export type ReportDesignItemTitleConfig = ReportDesignBaseConfig & {
  title?: string;
  size?: string;
  alignment?: string;
  fontWeight?: string;
};

export interface ReportDesignItemCustomerSignature extends ReportDesignItemTemplate {}

export interface ReportDesignItemSignature extends ReportDesignItemTemplate {
  config: ReportDesignItemSignatureConfig;
}

export type ReportDesignItemCustomerSignatureConfig = ReportDesignBaseConfig & {
  align: string | undefined;
};

export type ReportDesignItemSignatureConfig = ReportDesignBaseConfig & {
  user: string | undefined;
  align: string | undefined;
  value: string | undefined;
};

export interface ReportDesignItemSignatureValue extends ReportDesignItemSignature {
  signatureData: string;
}

export interface ReportDesignItemText extends ReportDesignItemTemplate {
  config: ReportDesignItemTextConfig;
}

export type ReportDesignItemTextConfig = ReportDesignBaseConfig & {
  title?: string;
  multiline?: boolean;
  required?: boolean;
  dataType?: string;
  quickview?: boolean;
};

export interface ReportDesginItemChecklist extends ReportDesignItemTemplate {
  config: ReportDesignItemChecklistConfig;
}

export interface ReportDesignItemChecklistRendered extends ReportDesginItemChecklist {
  value?: string;
}

export type ReportDesignItemChecklistConfig = ReportDesignBaseConfig & {
  rows?: string;
  columns?: string;
  groups?: string;
  quickview?: boolean;
};

export interface ReportDesignItemCheck extends ReportDesignItemTemplate {
  config: ReportDesignItemCheckConfig;
}

export interface ReportDesignItemCheckRendered extends ReportDesignItemCheck {
  value?: string;
}

export type ReportDesignItemCheckConfig = ReportDesignBaseConfig & {
  text?: string;
  quickview?: boolean;
};

export interface ReportDesignItemEmpty extends ReportDesignItemTemplate {}

export interface ReportDesignItemTextArea extends ReportDesignItemTemplate {
  config: ReportDesignItemTextAreaConfig;
}

export interface ReportDesignItemTextAreaRendered extends ReportDesignItemTextArea {
  value?: string;
}

export type ReportDesignItemTextAreaConfig = ReportDesignBaseConfig & {
  text?: string;
  quickview?: boolean;
};

export interface ReportDesignItemSelectbox extends ReportDesignItemTemplate {
  config: ReportDesignItemSelectboxConfig;
}

export interface ReportDesignItemSelectboxValue extends ReportDesignItemSelectbox {
  value?: string;
}

export type ReportDesignItemSelectboxConfig = ReportDesignBaseConfig & {
  title?: string;
  options?: string;
  quickview?: boolean;
};

export interface ReportDesignItemGrid extends ReportDesignItemTemplate {
  config: ReportDesignItemGridConfig;
}

export type ReportDesignItemGridConfig = ReportDesignBaseConfig & {
  cols: string;
  title: string;
  dataType: string;
  items: Array<{ title: string; property: string; dataType: string }>;
};

export interface ReportDesignItemTextValue extends ReportDesignItemTemplate {
  text?: string;
  value?: string;
}

export interface ReportDesignItemHeaderValue extends ReportDesignItemTemplate {
  image: string;
  title: string;
  subTitle: string;
}

export interface ReportDesignItemGridValue extends ReportDesignItemTemplate {
  values: Array<[string, string]>;
}

export type ReportDesignTablePropertyDataSourceConfig = Array<{
  value: string;
  display: string;
}>;

export function buildItemConfig(
  templateItem: ReportDesignItem
): ReportDesignItemConfig {
  const item = {
    ...templateItem,
    config: {
      ...(templateItem.baseConfig || {}),
      ...((<any>templateItem).config || {}),
      name: uuid()
    }
  };

  switch (item.type) {
    case "group-vertical": {
      item.config = <ReportDesignerItemGroupVerticalConfig>{
        ...item.config,
        group: <ReportDesignItemConfig[]>(item.config?.group || []),
        cols: "1"
      };
      break;
    }
    case "group":
    case "group-horizontal": {
      item.config = <ReportDesignerItemGroupHorizontalConfig>{
        ...item.config,
        group: <ReportDesignItemConfig[]>(item.config?.group || [])
      };
      break;
    }
    case "tab": {
      item.config = <ReportDesignerItemTabConfig>{
        ...item.config,
        title: templateItem.title,
        tabs: item.config.tabs || []
      };
      break;
    }
    case "image": {
      item.config = <ReportDesignerItemImageConfig>{
        ...item.config,
        data: "" //REPORT_DEISGNER_FAKE_IMAGE_DATA_URL
      };
      break;
    }
    case "text": {
      item.config = <ReportDesignItemTextConfig>{
        ...item.config
      };
      break;
    }
    case "signature": {
      item.config = <ReportDesignItemSignatureConfig>{
        ...item.config,
        title: "Dott. Mario Rossi",
        align: item.config?.align || "left",
        value: ""
      };
      break;
    }
    case "customer-signature": {
      item.config = <ReportDesignItemCustomerSignatureConfig>{
        ...(item.config || {}),
        align: item.config?.align || "left"
      };
      break;
    }
    case "selectbox": {
      item.config = <ReportDesignItemSelectboxConfig>{
        ...item.config,
        title: item.config?.title || "",
        options: item.config?.options || ""
      };
      break;
    }
  }

  return item;
}
